/**
 * Importing members from a spreadsheet: the mapping, the preview and the
 * summary.
 *
 * Pure, so the "used twice" rule and the three row errors can be tested
 * without a file picker. Spec: DESIGN-BRIEF.md §D and `vImport()` in
 * docs/design/netly-pay/prototype.html.
 */
import {samePhone, type NetlyPayData} from '../../internals/managers/NetlyPayManager';

export const IMPORT_STEPS: {label: string; sub: string}[] = [
  {label: 'Upload', sub: 'CSV or Excel'},
  {label: 'Map columns', sub: 'Match your headings'},
  {label: 'Preview', sub: 'Check each row'},
  {label: 'Import', sub: 'Save to members'},
];

/** The member fields a column can be mapped to. `skip` imports nothing. */
export const IMPORT_FIELDS: {value: string; label: string}[] = [
  {value: 'name', label: 'Full name *'},
  {value: 'phone', label: 'Phone *'},
  {value: 'guardian', label: 'Guardian name'},
  {value: 'guardianPhone', label: 'Guardian phone'},
  {value: 'dob', label: 'Date of birth'},
  {value: 'batch', label: 'Batch *'},
  {value: 'start', label: 'Start date'},
  {value: 'jersey', label: 'Jersey size'},
  {value: 'skip', label: "Don't import"},
];

export const REQUIRED_FIELDS = ['name', 'phone', 'batch'];

const FIELD_LABELS: Record<string, string> = {
  name: 'Full name',
  phone: 'Phone',
  batch: 'Batch',
};

export interface FileColumn {
  /** The heading as it appears in the file. */
  column: string;
  /** The first row's value, shown under the heading. */
  sample: string;
}

/**
 * A first guess at the mapping, by heading.
 *
 * Matched on words rather than on exact headings, because every academy names
 * its columns differently — "Student Name", "Full name" and "Name" are all
 * the name. Anything unrecognised is left on `skip` so nothing is imported
 * into the wrong field by accident.
 */
export function suggestMapping(columns: FileColumn[]): Record<string, string> {
  const guess = (heading: string): string => {
    const h = heading.toLowerCase();
    // The guardian rules come first and are checked for a phone before a
    // name: "Parent Name" contains "name", and letting that win would import
    // every parent as the member.
    if (/(guardian|parent|mother|father)/.test(h)) {
      return /(phone|mobile|contact|tel|no\.?$)/.test(h) ? 'guardianPhone' : 'guardian';
    }
    if (/(name)/.test(h)) return 'name';
    if (/(phone|mobile|contact|tel)/.test(h)) return 'phone';
    if (/(dob|birth)/.test(h)) return 'dob';
    if (/(batch|group|squad|class)/.test(h)) return 'batch';
    if (/(join|start|enrol)/.test(h)) return 'start';
    if (/(jersey|shirt|kit size)/.test(h)) return 'jersey';
    return 'skip';
  };
  const map: Record<string, string> = {};
  columns.forEach((c) => {
    map[c.column] = guess(c.column);
  });
  return map;
}

export interface MappingRow extends FileColumn {
  field: string;
  status: string;
  tone: 'pos' | 'danger' | 'muted';
  usedTwice: boolean;
}

export interface MappingState {
  rows: MappingRow[];
  /** Required fields nothing is mapped to yet. */
  missing: string[];
  missingText: string;
  /** Fields mapped more than once — the file cannot be read until they are not. */
  clashes: string[];
  canContinue: boolean;
}

/**
 * The mapping step's state.
 *
 * Two columns mapped to one field is an error rather than a last-wins choice:
 * one of them is the wrong column, and silently dropping either would import
 * a roster with the wrong phone numbers. `skip` is exempt — a file may well
 * have several columns nobody wants.
 */
export function mappingState(columns: FileColumn[], map: Record<string, string>): MappingState {
  const used = columns.map((c) => map[c.column] ?? 'skip');
  const counts: Record<string, number> = {};
  used.forEach((f) => {
    if (f !== 'skip') counts[f] = (counts[f] ?? 0) + 1;
  });

  const rows = columns.map((c) => {
    const field = map[c.column] ?? 'skip';
    const usedTwice = field !== 'skip' && (counts[field] ?? 0) > 1;
    const required = REQUIRED_FIELDS.includes(field);
    return {
      ...c,
      field,
      usedTwice,
      status: usedTwice ? '! Used twice' : field === 'skip' ? 'Skipped' : required ? '✓ Required' : '✓ Matched',
      tone: usedTwice ? 'danger' : field === 'skip' ? 'muted' : 'pos',
    } as MappingRow;
  });

  const missing = REQUIRED_FIELDS.filter((f) => !used.includes(f));
  const clashes = Object.keys(counts).filter((f) => counts[f] > 1);

  return {
    rows,
    missing,
    missingText: missing.length
      ? `Match a column to ${missing.map((f) => FIELD_LABELS[f] ?? f).join(', ')} to continue.`
      : '',
    clashes,
    canContinue: missing.length === 0 && clashes.length === 0,
  };
}

export type RowError = 'phone' | 'batch' | 'dup' | null;

export interface PreviewRow {
  n: number;
  name: string;
  phone: string;
  guardian: string;
  batch: string;
  dob: string;
  error: RowError;
  /** "✓ Ready", or what is wrong with this row in the owner's words. */
  check: string;
}

/**
 * The preview, one row per line in the file.
 *
 * Three errors, in the order the design lists them: a missing phone (requests
 * are sent there, so there is nothing to send), a batch name no batch has, and
 * a phone that already belongs to a member.
 *
 * The duplicate check looks at earlier rows in the FILE as well as at existing
 * members. A list exported twice and pasted together has duplicates inside
 * itself, and importing the second copy would create a second member with the
 * same phone — exactly the mess the duplicate check on the Add member form
 * exists to prevent.
 */
export function previewRows(
  rows: Record<string, string>[],
  map: Record<string, string>,
  data: NetlyPayData,
): PreviewRow[] {
  const members = data?.members ?? [];
  const batches = data?.batches ?? [];
  const byField = (row: Record<string, string>, field: string): string => {
    const column = Object.keys(map).find((c) => map[c] === field);
    return column ? String(row[column] ?? '').trim() : '';
  };

  const seen: {phone: string; name: string}[] = [];

  return rows.map((row, i) => {
    const name = byField(row, 'name');
    const phone = byField(row, 'phone');
    const guardian = byField(row, 'guardian');
    const batchName = byField(row, 'batch');
    const dob = byField(row, 'dob');

    const batch = batches.find((b) => b.name.toLowerCase() === batchName.toLowerCase());
    const existing = phone ? members.find((m) => samePhone(m.phone, phone)) : undefined;
    const earlier = phone ? seen.find((s) => samePhone(s.phone, phone)) : undefined;

    let error: RowError = null;
    let check = '✓ Ready';
    if (!phone) {
      error = 'phone';
      check = 'Phone is missing';
    } else if (!batch) {
      error = 'batch';
      check = batchName
        ? `No batch called ${batchName}. Pick a batch or fix the file.`
        : 'Batch is missing. Pick a batch or fix the file.';
    } else if (existing) {
      error = 'dup';
      check = `Same phone as ${existing.id} ${existing.name}`;
    } else if (earlier) {
      error = 'dup';
      check = `Same phone as ${earlier.name}, earlier in this file`;
    }

    seen.push({phone, name});

    return {
      n: i + 1,
      name,
      phone: phone || 'empty',
      guardian,
      batch: batchName || '—',
      dob,
      error,
      check: error ? `! ${check}` : check,
    };
  });
}

export interface ImportPlan {
  total: number;
  ready: number;
  errors: number;
  /** Nothing can be imported until the errors are fixed or skipped. */
  blocked: boolean;
  note: string;
}

export function importPlan(rows: PreviewRow[], skipErrors: boolean): ImportPlan {
  const errors = rows.filter((r) => r.error).length;
  const ready = rows.length - errors;
  return {
    total: rows.length,
    ready,
    errors,
    blocked: errors > 0 && !skipErrors,
    note: !errors
      ? `${ready} ${ready === 1 ? 'row is' : 'rows are'} ready to import.`
      : skipErrors
      ? `${errors} ${errors === 1 ? 'row' : 'rows'} will be skipped. You can download them after.`
      : `Fix the ${errors} ${errors === 1 ? 'row' : 'rows'} or skip them to continue.`,
  };
}

/** The summary after the run — what landed and what did not. */
export function importSummary(plan: ImportPlan, skipErrors: boolean): string {
  const added = `${plan.ready} ${plan.ready === 1 ? 'member' : 'members'} added`;
  if (!plan.errors) return `${added}.`;
  if (!skipErrors) return added;
  return `${added} · ${plan.errors} ${plan.errors === 1 ? 'row' : 'rows'} skipped`;
}

/** The template columns offered on the upload step. */
export const TEMPLATE_COLUMNS = [
  'Full name',
  'Phone',
  'Guardian name',
  'Guardian phone',
  'Date of birth',
  'Batch',
  'Start date',
  'Jersey size',
];

/** A CSV the browser can hand straight to the user, headings and one example. */
export function templateCsv(batchName = 'U12'): string {
  const example = [
    'Kasun Rathnayake',
    '077 410 2231',
    'Sujatha Rathnayake',
    '077 410 2231',
    '2015-02-14',
    batchName,
    '2026-10-01',
    'YM',
  ];
  return `${TEMPLATE_COLUMNS.join(',')}\n${example.join(',')}\n`;
}

/**
 * A pasted or uploaded CSV, as rows keyed by heading.
 *
 * Quoted fields are honoured, because a guardian's address or a medical note
 * routinely contains a comma, and splitting on every comma would shift every
 * later column by one.
 */
export function parseCsv(text: string): {columns: FileColumn[]; rows: Record<string, string>[]} {
  const lines = String(text ?? '')
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0);
  if (!lines.length) return {columns: [], rows: []};

  const split = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          quoted = !quoted;
        }
      } else if (ch === ',' && !quoted) {
        out.push(cur.trim());
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur.trim());
    return out;
  };

  const headings = split(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const cells = split(line);
    const row: Record<string, string> = {};
    headings.forEach((h, i) => {
      row[h] = cells[i] ?? '';
    });
    return row;
  });

  return {
    columns: headings.map((h) => ({column: h, sample: rows[0]?.[h] ?? ''})),
    rows,
  };
}
